<script setup lang="ts">
import { ref, onMounted, useTemplateRef, computed, onUnmounted, watch } from 'vue'
import Card from 'primevue/card'
import ScrollPanel from 'primevue/scrollpanel'
import Skeleton from 'primevue/skeleton'
import ProgressSpinner from 'primevue/progressspinner'
import { useRoute, useRouter } from 'vue-router'
import Button from 'primevue/button'
import EyeHeader from '@/components/EyeHeader.vue'
import type { OhlcvChartProps } from '@/components/OhlcvCard.vue'
import { getDexUrl, getTradeUrl, spreadHelper, toK, getBuyMarketType } from '@/utils'
import { buildSymbol, DEFAULT_QUOTE } from '@/utils/quoteHelpers'
import EyeOhlcvCard from '@/components/OhlcvCard.vue'
import { useThemeStore } from '@/stores/themeStore'
import isNull from 'lodash/isNull'


import UpdateAmountModal from '@/components/modals/UpdateAmountModal.vue'
// import type { CcxtMarket } from '@/services/types'
import ApiKeysModal from '@/components/modals/ApiKeysModal.vue'
import BellPopover from '@/components/BellPopover.vue'
import type { FormSubmitEvent } from '@primevue/forms'
// import CreateSwapOrderModal from '@/components/modals/CexDexOrdersModal.vue'
import { isUndefined, round } from 'lodash'
import SpreadCharts from '@/components/SpreadCharts.vue'
import UserPositionsModal from '@/components/modals/UserPositionsModal.vue'
// import LanguageSwitcher from '@/components/LanguageSwitcher.vue'
import { useFlasherStore } from '@/stores/flasherStore'
import DexNotFound from '../DexNotFound.vue'
import { useNavHeightRefresher } from '@/composable/refreshers/useNavHeightRefresher'
import ExchangePriceHeader from '@/components/ExchangePriceHeader.vue'
import { useCexDexSpread } from '@/composable/cex-dex/useCexDexSpread'
import type { MarketType } from '@/utils/types'
import { useTrailingSlSettings } from '@/composable/settings/useTrailingSlSettings'
import { useCexDexTicksHandler } from '@/composable/cex-dex/useCexDexTicksHandler'
import { useCexDexMarketData } from '@/composable/cex-dex/useCexDexMarketData'
import CexDexOrdersModal from '@/components/modals/CexDexOrdersModal.vue'
import { useCexPositionsRefresher } from '@/composable/refreshers/useCexPositionsRefresher'
import { TickUtils } from '@/utils/ticks'
import { OHLCV_TF } from '@/utils/consts'
import EyeDexPoolsDebugTable from '@/components/EyeDexPoolsDebugTable.vue'
import EyeDexTradesTable from '@/components/EyeDexTradesTable.vue'
import { useCheckUserRights } from '@/composable/useCheckUserRights'
// import CexDexSingleLegTriggerModal from '@/components/modals/CexDexSingleLegTriggerModal.vue'
// import CexDexSingleLegTriggersListModal from '@/components/modals/CexDexSingleLegTriggersListModal.vue'

// Сторы
const themeStore = useThemeStore()
const route = useRoute()
const flasherStore = useFlasherStore()

const navWrapper = ref<HTMLDivElement>()
const userProfilesModal = useTemplateRef('userProfilesModal')
const apiKeysModal = useTemplateRef('apiKeysModal')
const cexDexOrdersModal = useTemplateRef('cexDexOrdersModal')
const updateAmountModal = useTemplateRef('updateAmountModal')
// const cexDexSingleLegTriggerModal = useTemplateRef('cexDexSingleLegTriggerModal')
// const cexDexSingleLegTriggersListModal = useTemplateRef('cexDexSingleLegTriggersListModal')
const { navHeight, navHeightRefresher } = useNavHeightRefresher(navWrapper)
const homeRouteName = computed(() => marketType.value == "swap" ? "signals-dex-cex-swap" : "signals-dex-cex-spot")
const router = useRouter()
const { isDexCexAdvanced } = useCheckUserRights()

// Параметры
const marketType = computed(() => (route.params.marketType ?? "swap") as MarketType)
const cex = computed(() => route.params.cex?.toString().toLocaleLowerCase() as string)
const asset = computed(() => route.params.asset?.toString().toUpperCase() as string)
const quoteBuy = computed(() => (route.query.qb as string) || DEFAULT_QUOTE)
const quoteSell = computed(() => (route.query.qs as string) || DEFAULT_QUOTE)
const cexSymbol = computed(() => buildSymbol(asset.value, quoteBuy.value, getBuyMarketType(marketType.value)))
const chain = computed(() => route.params.chain as string)
const amount = computed(() => (route.params.amount ? Number(route.params.amount) : 10))
const commonUrl = computed(() => router.resolve({
  name: 'eye-all-asset',
  params: {
    asset: asset.value,
    chain: chain.value,
  },
}).fullPath)
const cacheKeyPostfix = computed(() => `dex/${cex.value}/${asset.value}/${quoteBuy.value}/${quoteSell.value}`.toLocaleLowerCase())

// Композаблы
const { contract, fetchContractData, commonRounding, fetchMarket, market } = useCexDexMarketData(cex, cexSymbol, chain)
// const poolAddress = computed(() => route.query["pool"] ?? validPools.value[0].contract)
// const allowedPools = computed(() => route.query["pool"] ? (route.query["pool"] as string).split(";") : [])
const { loadingLp, cexPriceBuyBroken, cexPriceSellBroken, checkBellRefresher, startWebSocket, stopWebSocket, spread, backSpread, dexPriceAuto, cexPriceAuto, dexPrice, cexPriceBuy, cexPriceSell, cexLastPrice, dexVol, compotedDexPools, dexPriceBuy, dexPriceSell, dexTrades, poolSwap, biggestPoolUpdateTs, gmgnPriceUsed } = useCexDexSpread(chain, contract, cex, asset, marketType, amount, quoteBuy)
const { trailingExit, trailingNext } = useTrailingSlSettings(cacheKeyPostfix)
const { positionsRefresher, updatePositions } = useCexPositionsRefresher(cex, "dex_cex")
const { cexTicks, dexTicks, cexAsks, cexBids, fetchCexChartData, fetchDexChartData, ticksRefresher, dexAsks, dexBids } = useCexDexTicksHandler(
  cexLastPrice, dexPrice, cexPriceBuy, cexPriceSell, dexPriceBuy, dexPriceSell
)

const minMove = computed(() => {
  return 1 / Math.pow(10, commonRounding.value)
})
// UI-переменные
const eyeCardHeight = ref('350px')
const cexChartProps = ref<OhlcvChartProps>({
  loading: true,
  hideVol24h: false,
  hideFundingRate: true,
  minMove: 0.0001,
  marketType: 'swap',
})
const dexChartProps = ref<OhlcvChartProps>({
  loading: true,
  hideVol24h: false,
  hideFundingRate: true,
  minMove: 0.0001,
  marketType: 'spot',
})
const poolNotFound = ref(false)
// const showTrades = ref(false)
const dexPoolDelta = computed(() => (+new Date() / 1000) - (biggestPoolUpdateTs.value ?? 0))
const isDexDataStalled = computed(() => dexPoolDelta.value >= 60)
const colorSchemeIcon = computed(() => (themeStore.mode == 'dark' ? 'pi pi-moon' : 'pi pi-sun'))
const recommendedAmount = computed(() => {
  const step = market.value?.contractSize ?? 1
  if (!dexPrice.value) {
    return
  }
  let am = Math.round(amount.value / Number(dexPrice.value) / step) * step
  if (am == 0) {
    am = step
  }
  return Number.isFinite(am) ? am : null
})
const tabTitle = computed(() => {
  if (asset.value && cex.value) {
    const biggerSpread = spread.value && backSpread.value ? Math.max(spread.value, backSpread.value) : null
    const parts = [
      'DEX',
      !isNull(biggerSpread) ? Number(biggerSpread).toFixed(2) + '%' : '-',
      asset.value.toUpperCase(),
      cex.value.toUpperCase(),
    ]
    if (flasherStore.flash) {
      return '[!!!] ' + parts.join(' / ')
    }
    return parts.join(' / ')
  }
  return 'Fobium.Trade'
})

watch(dexPrice, () => {
  const ohlcv = TickUtils.ticksToOhlcv(dexTicks.value, OHLCV_TF)
  dexChartProps.value.ohlcv = ohlcv
})

watch(cexLastPrice, () => {
  const ohlcv = TickUtils.ticksToOhlcv(cexTicks.value, OHLCV_TF)
  cexChartProps.value.ohlcv = ohlcv
})

const updateDexChart = async () => {
  const chartData = await fetchDexChartData(chain.value, contract.value)
  dexChartProps.value.loading = false
  if (!chartData) {
    dexChartProps.value.error = true
    return
  }
  dexChartProps.value = {
    ...dexChartProps.value,
    ...chartData,
  }
}

const updateCexChart = async () => {
  const chartData = await fetchCexChartData(cex.value, cexSymbol.value)
  cexChartProps.value.loading = false
  if (!chartData) {
    cexChartProps.value.error = true
    return
  }
  cexChartProps.value = {
    ...cexChartProps.value,
    ...chartData,
  }
}

// Вотчеры
watch(dexVol, (v) => {
  dexChartProps.value.vol24h = toK(v ?? 0)
})
watch(tabTitle, (v) => {
  if (v) {
    window.document.title = v
  }
})
watch(poolSwap, (v) => {
  dexChartProps.value.exchange = v
  dexChartProps.value.titleHref = getDexUrl(v, chain.value, contract.value)
})

// Жизненный цикл приложения
onMounted(async () => {

  // alert(marketType.value)
  // console.log(`marketType.value: ${marketType.value}`)
  if (!["spot", "swap"].includes(marketType.value)) {
    const href = router.resolve({
      name: "eye-dex-asset-long",
      params: {
        marketType: "swap",
        cex: cex.value,
        asset: asset.value,
        chain: chain.value,
        amount: amount.value
      }
    })
    window.location.href = href.fullPath
    return
  }

  // Грузим информацию о контрактах, графиках
  await fetchContractData()

  if (!contract.value) {
    poolNotFound.value = true
    loadingLp.value = false
    return
  }

  checkBellRefresher.startRefresh()
  navHeightRefresher.startRefresh()
  ticksRefresher.startRefresh()
  positionsRefresher.startRefresh()

  // Ставим имена графикам
  cexChartProps.value.marketType = marketType.value
  cexChartProps.value.exchange = cex.value
  cexChartProps.value.titleHref = getTradeUrl(cex.value, asset.value, marketType.value == 'swap', quoteBuy.value)

  await Promise.allSettled([
    updateCexChart(),
    updateDexChart(),
    fetchMarket()
  ])

  // Запускает real-time обновления
  startWebSocket()

})

onUnmounted(() => {
  stopWebSocket()
  positionsRefresher.stopRefresh()
  navHeightRefresher.stopRefresh()
  checkBellRefresher.stopRefresh()
  ticksRefresher.stopRefresh()
})

// UI-колбэки
const onAmountFormSubmit = (r: FormSubmitEvent) => {
  const valid = r.valid
  const values = r.values
  if (valid) {
    window.location.href = router.resolve({
      name: route.name,
      params: {
        cex: cex.value,
        asset: asset.value,
        pool: route.params.pool,
        amount: values.amount,
      },
    }).href
  }
}

const showPositions = () => {
  userProfilesModal.value?.toggle()
}

const isDebug = computed(() => route.query.debug !== undefined)
const showTrades = computed(() => isDebug.value)
const useMarketTradesForPrice = () => {
  loadingLp.value = false
}
</script>

<template>
  <div>
    <DexNotFound v-if="poolNotFound"></DexNotFound>
    <div v-show="!poolNotFound">
      <div ref="navWrapper" class="sr-navbar-container">
        <EyeHeader>
          <template #start>
            <span v-if="recommendedAmount" @click="updateAmountModal?.toggle()" class="pit digit amount">
              {{ recommendedAmount }} {{ asset.toUpperCase() }} (${{ Number(amount).toFixed(0) }})
            </span>
            <template v-else>
              <Skeleton width="100px" height="22px"></Skeleton>
              <Skeleton class="ms-3" width="50px" height="22px"></Skeleton>
            </template>
          </template>

          <!-- Торговая пара -->
          <template #symbol>
            <a :href="commonUrl" target="_blank" class="uppercase pit hover" v-if="asset">{{ asset }}</a>
            <Skeleton v-else height="24px" width="90px"></Skeleton>
          </template>

          <!-- Спред -->
          <template #spread>
            <div class="flex justify-center items-center" :style="`height: 30px`">
              <template v-if="isNull(spread) || isUndefined(spread)">
                <Skeleton v-if="!cexPriceBuyBroken" height="22px" width="50px"></Skeleton>
                <span v-tooltip.bottom="$t('cexDataBrokenError')" class="font-bold" v-else>!!!</span>
              </template>
              <span v-else class="digit"
                :class="{ 'sr-eyebar-backspread': spread < 0.1, 'sr-eyebar-spread': spread >= 0.1 }">{{
                  spreadHelper(spread) }}%</span>

              <template v-if="isNull(backSpread) || isUndefined(backSpread)">
                <Skeleton v-if="!cexPriceSellBroken" class="ms-2" height="22px" width="50px"></Skeleton>
                <span v-tooltip.bottom="$t('cexDataBrokenError')" class="font-bold ms-2" v-else>!!!</span>
              </template>
              <span v-else class="ms-2 digit" :class="{
                'sr-eyebar-backspread': backSpread < 0.1,
                'sr-eyebar-spread': backSpread >= 0.1,
              }">{{ spreadHelper(backSpread) }}%</span>
            </div>
          </template>

          <!-- Биржа CEX -->
          <template #exBuy>
            <ExchangePriceHeader :exchange="cexChartProps.exchange" :title-href="cexChartProps.titleHref"
              class="sr-eye-ex-buy" :price="Number(cexPriceAuto?.price ?? 0)" :vol="Number(cexPriceAuto?.vol ?? 0)"
              :loading="cexPriceAuto?.price == null"
              :price-class="spread && backSpread && spread > backSpread ? 'mark-buy' : 'mark-sell'">
            </ExchangePriceHeader>
          </template>

          <!-- Биржа DEX -->
          <template #exSell>
            <ExchangePriceHeader :exchange="dexChartProps.exchange" :title-href="dexChartProps.titleHref"
              class="sr-eye-ex-sell" :price="round(dexPriceAuto ?? 0, commonRounding)" :loading="dexPriceAuto == null"
              :vol="null" :price-class="spread && backSpread && spread > backSpread ? 'mark-sell' : 'mark-buy'">
              >
              <template #right>
                <span v-tooltip.bottom="$t('gmgnPriceUsed')" class="pit danger ms-2" v-if="gmgnPriceUsed">M</span>
                <span v-tooltip.bottom="$t('dexDataStalledError')" class="pit danger ms-2"
                  v-else-if="isDexDataStalled">!!!</span>
              </template>
            </ExchangePriceHeader>
          </template>

          <template #end>
            <div class="sr-eye-navbar-end gap-1 flex items-center">
              <!-- <LanguageSwitcher size="small" /> -->

              <Button size="small" v-tooltip.bottom="$t('goHome')" icon="pi pi-home"
                @click="router.push({ name: homeRouteName })" variant="text" rounded aria-label="Home" />

              <Button size="small" v-tooltip.bottom="$t('changeColorScheme')" :icon="colorSchemeIcon"
                @click="themeStore.toggle()" variant="text" rounded aria-label="Theme" />

              <BellPopover rounded size="small" :spread-label="$t('longSpread')" :back-spread-label="$t('shortSpread')">
              </BellPopover>

              <Button size="small" v-tooltip.bottom="$t('apiKeys')" icon="pi pi-key" @click="apiKeysModal?.toggle()"
                variant="text" rounded aria-label="Api Keys" />

              <Button size="small" v-tooltip.bottom="$t('placeOrder')" :disabled="!Boolean(market)" icon="pi pi-bolt"
                @click="cexDexOrdersModal?.toggle()" variant="text" rounded aria-label="Create order" />

              <!-- <Button size="small" v-tooltip.bottom="$t('modal.cexDexSingleLegTrigger.title')" icon="pi pi-server"
                @click="cexDexSingleLegTriggerModal?.toggle()" variant="text" rounded aria-label="Create CEX-DEX trigger" />

              <Button size="small" v-tooltip.bottom="$t('modal.cexDexSingleLegTriggers.title')" icon="pi pi-list"
                @click="cexDexSingleLegTriggersListModal?.toggle()" variant="text" rounded aria-label="CEX-DEX triggers list" /> -->

              <!-- <Button size="small" v-tooltip.bottom="$t('dexTrades.title')" icon="pi pi-list"
                @click="showTrades = !showTrades" variant="text" rounded aria-label="Dex trades" /> -->
            </div>
          </template>
        </EyeHeader>
      </div>
      <UserPositionsModal :exchanges="[cex]" :symbols="[cexSymbol]" ref="userProfilesModal">
      </UserPositionsModal>
      <UpdateAmountModal @submit="onAmountFormSubmit" ref="updateAmountModal"></UpdateAmountModal>
      <ApiKeysModal :pro="isDexCexAdvanced" keys-type="dex_cex" ref="apiKeysModal"></ApiKeysModal>
      <!-- <CexDexSingleLegTriggerModal
        :cex="cex"
        :chain="chain"
        :pool-address="contract"
        :asset="asset"
        :market-type="marketType"
        ref="cexDexSingleLegTriggerModal"
      />
      <CexDexSingleLegTriggersListModal ref="cexDexSingleLegTriggersListModal" /> -->
      <CexDexOrdersModal :dex-vol="dexVol" :price-cex="cexLastPrice" :price-dex="dexPrice" :leverage-min="1"
        :cex-ticks="cexTicks" :dex-movement-size="1" :price-buy="cexPriceBuy?.price ?? null"
        :price-sell="cexPriceSell?.price ?? null" :dex-price-buy="dexPriceBuy" :dex-price-sell="dexPriceSell"
        v-if="market" :market="market" :asset="asset" :cex="cex" ref="cexDexOrdersModal" :spread="spread"
        :contract="contract" :back-spread="backSpread" :recommended-amount="recommendedAmount" :chain="chain"
        :dex-ticks="dexTicks" :cache-key="cacheKeyPostfix" :quote-buy="quoteBuy" @show-positions-modal="showPositions"
        @update-user-positions="updatePositions">
      </CexDexOrdersModal>
      <main class="mx-0 mt-0 grid grid-cols-1" :style="{ paddingTop: navHeight + 'px' }">
        <Card pt:body:class="p-0" pt:root:class="rounded-none" style="border: none;">
          <template #content>
            <SpreadCharts :ticks-left="cexTicks" :ticks-right="dexTicks" :min-move="minMove" :show-ab-ba="true"
              ex-buy="CEX" ex-sell="DEX" class="col-span-2 border-color border-b-1" :asks-left="cexAsks"
              :bids-left="cexBids" :sl="trailingExit" :asks-right="dexAsks" :bids-right="dexBids" :tp="trailingNext"
              :legend="[
                { name: 'ticks-left', label: 'legend.cex' },
                { name: 'ticks-right', label: 'legend.dex' },
                { name: 'ask-left', label: 'legend.cexBuy' },
                { name: 'bid-left', label: 'legend.cexSell' },
                { name: 'ask-right', label: 'legend.dexBuy' },
                { name: 'bid-right', label: 'legend.dexSell' },
              ]">
            </SpreadCharts>
          </template>
        </Card>
        <div class="grid grid-cols-1" :class="{ 'lg:grid-cols-3': showTrades, 'lg:grid-cols-2': !showTrades }">
          <Card pt:body:class="p-0" pt:root:class="rounded-none"
            style="border-left: none;border-right: none;border-top: none;">
            <template #content>
              <EyeOhlcvCard v-bind="cexChartProps" title-class="success capitalize" y-format="1s" :min-move="minMove"
                class="col-span-2 lg:col-span-1 lg:border-r-1 border-color" :height="eyeCardHeight">
              </EyeOhlcvCard>
            </template>
          </Card>
          <Card pt:body:class="p-0" pt:root:class="rounded-none" style="border-left: none;border-top: none;"
            :class="{ 'brn': !showTrades }">
            <template #content>
              <EyeOhlcvCard v-bind="dexChartProps" title-class="danger capitalize" :min-move="minMove"
                class="col-span-2 lg:col-span-1 border-t-1 border-color lg:border-t-0" y-format="1s"
                :height="eyeCardHeight">
              </EyeOhlcvCard>
            </template>
          </Card>
          <Card v-if="showTrades" pt:body:class="p-0" pt:root:class="rounded-none"
            style="border-left: none;border-right: none;border-top: none;">
            <template #content>
              <div class="flex justify-between chart-title py-2 px-4">
                <span class="pit">{{ $t('dexTrades.title') }}</span>
              </div>
              <ScrollPanel>
                <div style="max-height: 330px;">
                  <EyeDexTradesTable :trades="dexTrades" :rounding="commonRounding" />
                </div>
              </ScrollPanel>
            </template>
          </Card>
        </div>
        <Card pt:body:class="p-0" pt:root:class="rounded-none" v-if="isDebug">
          <template #content>
            <div class="p-2 text-sm font-mono">
              min move: {{ minMove }}
              commonRounding: {{ commonRounding }}
              <div><strong>Weighted Avg Buy:</strong> {{ dexPriceBuy !== null ?
                dexPriceBuy.toFixed(8) : '—' }}</div>
              <div><strong>Weighted Avg Sell:</strong> {{ dexPriceSell !== null ?
                dexPriceSell.toFixed(8) : '—' }}</div>
            </div>
            <EyeDexPoolsDebugTable :pools="compotedDexPools" />
          </template>
        </Card>
      </main>
    </div>

    <!-- Pool Initialization Overlay -->
    <div v-if="loadingLp"
      class="fixed inset-0 z-50 flex flex-col items-center justify-center bg-black/60 backdrop-blur-sm">
      <ProgressSpinner style="width: 50px; height: 50px" strokeWidth="4" animationDuration="1s" />
      <span class="mt-4 text-lg font-semibold text-white mb-4">
        {{ $t('poolInitLoading') }}
      </span>
      <Button @click="useMarketTradesForPrice" v-tooltip.bottom="$t('useDexLastMarketPriceTooltip')" size="small"
        severity="secondary">{{
          $t('useDexLastMarketPrice') }}</Button>
    </div>
  </div>
</template>

<style scoped>
.brn {
  border-right: none
}
</style>
